/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState, useEffect, useCallback, useRef } from 'react'
import { Helmet } from 'react-helmet'
import { useLocation, useNavigate } from 'react-router-dom'
import { getBranches } from 'app/api/maracana'
import LoadingContent from 'app/modules/loading/LoadingContent'
import { getUserPosition } from 'app/utils/geolocation'
import {
  getStoredUserLocation,
  setStoredUserLocation,
} from 'app/utils/userLocation'
import { KTSVG } from '../../../_metronic/helpers'
import UnitUnavailableModal from 'app/utils/UnitUnavailableModal'

const isWebView = () => typeof (window as any).ReactNativeWebView !== 'undefined'

type Branch = {
  id: string | number
  name?: string
  address?: string
  slug?: string
  distance?: number
  photo?: string
  verified?: boolean
  attributes?: {
    name?: string
    address?: string
    slug?: string
    distance?: number
    photo?: string
    verified?: boolean
  }
}

const getBranchName = (branch: Branch) =>
  branch?.attributes?.name ?? branch?.name ?? 'Sem nome'

const getBranchAddress = (branch: Branch) =>
  branch?.attributes?.address ?? branch?.address ?? ''

const getBranchDistance = (branch: Branch) =>
  branch?.attributes?.distance ?? branch?.distance

const getBranchPhoto = (branch: Branch) =>
  branch?.attributes?.photo ?? branch?.photo

const isBranchVerified = (branch: Branch) =>
  Boolean(branch?.attributes?.verified ?? branch?.verified)

const getBranchSlug = (branch: Branch) =>
  branch?.attributes?.slug ?? branch?.slug

type HomeLocationState = {
  latitude?: number
  longitude?: number
  unitUnavailable?: boolean
}

const HomePage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [branches, setBranches] = useState<Branch[]>([])
  const [loading, setLoading] = useState(true)
  const unitUnavailableRef = useRef(
    Boolean((location.state as HomeLocationState | null)?.unitUnavailable)
  )
  const [showUnitUnavailableModal, setShowUnitUnavailableModal] = useState(
    unitUnavailableRef.current
  )

  const navigateToRequestLocation = useCallback(() => {
    navigate(
      '/request-location',
      unitUnavailableRef.current ? { state: { unitUnavailable: true } } : undefined
    )
  }, [navigate])

  const fetchBranchesByLocation = useCallback((lat: number, lng: number) => {
    setStoredUserLocation(lat, lng)
    setLoading(true)
    getBranches({
      lat,
      long: lng,
      distance: 50,
      verified: 1,
    })
      .then((response: any) => {
        const data = response?.data ?? response ?? []
        setBranches(Array.isArray(data) ? data : [])
      })
      .catch(() => setBranches([]))
      .finally(() => setLoading(false))
  }, [])

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      navigateToRequestLocation()
      return
    }

    setLoading(true)
    getUserPosition()
      .then((position) => {
        fetchBranchesByLocation(
          position.coords.latitude,
          position.coords.longitude
        )
      })
      .catch(() => navigateToRequestLocation())
  }, [fetchBranchesByLocation, navigateToRequestLocation])

  useEffect(() => {
    const state = location.state as HomeLocationState | null
    const stateLat = state?.latitude
    const stateLng = state?.longitude

    if (stateLat != null && stateLng != null) {
      fetchBranchesByLocation(stateLat, stateLng)
      return
    }

    const stored = getStoredUserLocation()
    if (stored) {
      fetchBranchesByLocation(stored.latitude, stored.longitude)
      return
    }

    if (isWebView()) {
      let resolved = false
      let timeoutId: ReturnType<typeof setTimeout>

      const handler = (e: MessageEvent) => {
        try {
          const msg = typeof e.data === 'string' ? JSON.parse(e.data) : e.data
          if (msg?.type !== 'location-permission-result' || resolved) return

          resolved = true
          clearTimeout(timeoutId)

          if (msg.data?.granted && msg.data.latitude != null) {
            fetchBranchesByLocation(msg.data.latitude, msg.data.longitude)
          } else {
            setLoading(false)
            navigateToRequestLocation()
          }
        } catch {}
      }
      window.addEventListener('message', handler)

      // Aguarda o Sansiro enviar localização (getCurrentPosition pode demorar no Android)
      timeoutId = setTimeout(() => {
        if (!resolved) {
          resolved = true
          setLoading(false)
          navigateToRequestLocation()
        }
      }, 5000)

      return () => {
        window.removeEventListener('message', handler)
        clearTimeout(timeoutId)
      }
    }

    requestLocation()
  }, [location.pathname, location.state, fetchBranchesByLocation, requestLocation, navigateToRequestLocation])

  const showBranchItem = (branch: Branch) => {
    const photo = getBranchPhoto(branch)
    const distance = getBranchDistance(branch)
    const verified = isBranchVerified(branch)
    const slug = getBranchSlug(branch)

    return (
      <div className="col-lg-6 mb-1 mb-lg-0" key={branch.id}>
        <div className="d-flex justify-content-center">
          <div
            className="card card-dashed flex-row flex-stack p-4 mb-6 w-100 cursor-pointer"
            onClick={() => slug && navigate(`/unidade/${slug}`)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (slug && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault()
                navigate(`/unidade/${slug}`)
              }
            }}
          >
            <div className="d-flex align-items-center me-3">
              {photo ? (
                <div className="symbol symbol-60px symbol-fixed">
                  <img src={photo} alt="" className="object-cover" />
                </div>
              ) : (
                <div className="symbol symbol-60px symbol-fixed bg-light-primary">
                  <KTSVG path="/media/icons/duotune/general/gen021.svg" className="svg-icon-2x svg-icon-primary" />
                </div>
              )}
            </div>
            <div className="d-flex align-items-center me-2 flex-grow-1">
              <div>
                <div className="d-flex align-items-center fs-2 fw-bold flex-wrap pb-1">
                  {getBranchName(branch)}
                  {verified && (
                    <span className="ms-2" title="Verificado">
                      <KTSVG
                        path="/media/icons/duotune/general/gen026.svg"
                        className="svg-icon-1 svg-icon-primary"
                      />
                    </span>
                  )}
                </div>
                <div className="fs-7 fw-semibold text-gray-700 pb-1">
                  {getBranchAddress(branch)}
                </div>
                {(typeof distance === 'number' || (distance != null && !isNaN(Number(distance)))) && (
                  <div className="fs-7 fw-semibold text-muted">
                    {Number(distance).toFixed(1)} km
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <UnitUnavailableModal
        show={showUnitUnavailableModal}
        onClose={() => setShowUnitUnavailableModal(false)}
      />

      <Helmet>
        <title>Home - Parceiros próximos - Atacante App</title>
      </Helmet>

      <div className="card mb-5 mb-xl-10">
        <div className="card-body pt-9 pb-0 mb-13">
          <div className="mb-10">
            <h1 className="mb-3">Parceiros próximos</h1>
            <div className="text-muted fw-semibold fs-6">
              Encontre parceiros perto de você
            </div>
          </div>

          {loading && <LoadingContent />}

          {!loading && branches.length > 0 && (
            <div className="row mt-10">{branches.map(showBranchItem)}</div>
          )}

          {!loading && branches.length === 0 && (
            <div className="my-10 text-center">
              <h2 className="mb-3">Nenhum parceiro encontrado</h2>
              <div className="text-muted fw-semibold fs-5">
                Não encontramos parceiros próximos à sua localização.
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export { HomePage }
